import {tv} from 'tailwind-variants'
import {grid, gridItem} from '~/design-system/foundations'
import {HgAspectRatio} from '~/design-system/hg/components'
import HgArticleHeroText, {
  type HgArticleHeroTextProps,
} from '~/design-system/hg/components/HgArticleHeroText'
import {type AuraColorToken} from '~/design-system/hg/tokens/colors'
import {getHexValueFromToken} from '~/design-system/hg/tokens/colors/utils'
import {cn, unreachableCase} from '~/design-system/utils'

export type HgArticleHeroMediumVariantProps = {
  assetType: 'horizontal' | 'square' | 'vertical'
  asset: React.ReactNode
  className?: string
  backgroundColorToken?: AuraColorToken
} & HgArticleHeroTextProps

const hgArticleHeroMediumVariants = tv({
  slots: {
    asset: '',
    textContainer: '',
    innerWrapper: '',
    outerWrapper: '',
  },
  variants: {
    assetType: {
      horizontal: {
        asset: gridItem({
          size: 'max',
        }),
        innerWrapper: grid({className: 'mx-auto'}),
        textContainer: gridItem({
          size: 'max',
          className: 'pb-s6 pt-s8',
        }),
      },
      square: {
        asset: 'col-span-full md:col-span-10 md:col-start-2 lg:col-start-4',
        innerWrapper: grid({className: 'mx-auto'}),
        textContainer: gridItem({
          size: 'max',
          className: 'pb-s6 pt-s8',
        }),
      },
      vertical: {
        asset: 'w-full',
        innerWrapper:
          'grid place-items-center gap-s5 px-s5 pt-s5 sm:grid-cols-2 sm:gap-s4',
      },
    },
    hasBackgroundColor: {
      true: {},
    },
  },
  compoundVariants: [
    {
      assetType: 'horizontal',
      hasBackgroundColor: true,
      className: {
        outerWrapper: 'pb-s8',
      },
    },
    {
      assetType: 'square',
      hasBackgroundColor: true,
      className: {
        outerWrapper: 'pb-s8',
      },
    },
    {
      assetType: 'vertical',
      hasBackgroundColor: true,
      className: {
        outerWrapper: 'pb-s5',
      },
    },
  ],
})

export default function HgArticleHeroMediumVariantComponents({
  className,
  asset,
  assetType,
  backgroundColorToken,
  ...articleHeroTextProps
}: HgArticleHeroMediumVariantProps) {
  const backgroundColor = getHexValueFromToken(backgroundColorToken)
  const {
    innerWrapper,
    textContainer,
    asset: assetVariant,
    outerWrapper,
  } = hgArticleHeroMediumVariants({
    assetType,
    hasBackgroundColor: !!backgroundColor,
  })

  const getAspectRatio = () => {
    switch (assetType) {
      case 'horizontal':
        return '16:9'
      case 'square':
        return '1:1'
      case 'vertical':
        return '4:5'
      default:
        return unreachableCase(assetType)
    }
  }

  return (
    <div
      style={{'--background': backgroundColor} as React.CSSProperties}
      className={cn(
        !!backgroundColor && 'bg-[var(--background)]',
        'pt-blog-navbars-height'
      )}
    >
      <div className={outerWrapper()}>
        <div className={innerWrapper({className})}>
          <div className={textContainer()}>
            <HgArticleHeroText
              classNames={
                assetType !== 'vertical'
                  ? {
                      container: 'lg:grid lg:grid-cols-12 lg:gap-x-32',
                      tag: 'lg:col-span-full',
                      heading: 'lg:col-span-full',
                      subheading: 'md:px-s8 lg:col-span-8 lg:col-start-3 lg:px-0',
                    }
                  : undefined
              }
              {...articleHeroTextProps}
            />
          </div>
          <HgAspectRatio
            classNames={{wrapper: assetVariant()}}
            aspectRatio={getAspectRatio()}
            borderRadius="0px"
          >
            {asset}
          </HgAspectRatio>
        </div>
      </div>
    </div>
  )
}
